import { useEffect, useMemo, useState } from "react";
import { useT } from "../features/i18n/useT";
import { usePageTag } from "../app/usePageTag";
import { useEvents } from "../features/calendar/useEvents";
import type { AppEvent } from "../features/calendar/types";
import { useDone } from "../features/done/useDone";
import { openExternalUrl } from "../shared/utils/safeUrl";
import { openMapWithRoom } from "../features/map/roomLink";
import { OverflowMenu } from "../shared/components/OverflowMenu";

type Tab = "active" | "done";
type SortMode = "due" | "overdueTop";

function isDeadlineLike(e: AppEvent) {
  return e.kind === "deadline" || e.kind === "assignment";
}

function openMap(room?: string) {
  if (!room) return;
  openMapWithRoom(room);
}

function fmtWhen(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms: number) {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatCountdown(dueIso: string, now: number) {
  const due = new Date(dueIso).getTime();
  if (!Number.isFinite(due)) return "";
  const diff = due - now;
  if (diff >= 0) return `in ${formatDuration(diff)}`;
  return `over ${formatDuration(-diff)}`;
}

function isOverdue(dueIso: string, now = Date.now()) {
  const t = new Date(dueIso).getTime();
  return Number.isFinite(t) && t < now;
}

function extractUrls(text: string): string[] {
  const matches = text.match(/https?:\/\/[^\s)>\]]+/gi) || [];
  return Array.from(new Set(matches));
}

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
}

export default function DeadlinesPage() {
  usePageTag("deadlines");
  const t = useT();
  const events = useEvents();
  const { isDone, markDone, unDone } = useDone();

  const [tab, setTab] = useState<Tab>("active");
  const [sort, setSort] = useState<SortMode>("due");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const todayStart = useMemo(() => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [now]);


  const deadlines = useMemo(() => {
    const list = events.filter(isDeadlineLike);
    const mapped = list.map((e) => ({ ...e, dueAt: e.due || e.start }));
    return mapped;
  }, [events]);

  const active = useMemo(() => {
    const list = deadlines.filter((d) => !isDone(d.uid)).filter((d) => new Date(d.dueAt).getTime() >= todayStart);
    return list
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.dueAt).getTime();
        const tb = new Date(b.dueAt).getTime();

        if (sort === "overdueTop") {
          const oa = ta < now;
          const ob = tb < now;
          if (oa !== ob) return oa ? -1 : 1;
        }
        return ta - tb;
      });
  }, [deadlines, isDone, sort, now, todayStart]);

  const done = useMemo(() => {
    const list = deadlines.filter((d) => isDone(d.uid));
    return list
      .slice()
      .sort((a, b) => new Date(b.dueAt).getTime() - new Date(a.dueAt).getTime());
  }, [deadlines, isDone]);

  const list = tab === "active" ? active : done;
  const toggleDesc = (uid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  return (
    <div className="page deadlines-page">
      <div className="row wrap" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: "6px 0 10px" }}>{t("deadlines.title")}</h2>

        <OverflowMenu
          items={[
            {
              label: `${sort === "due" ? "* " : ""}${t("deadlines.sort.due")}`,
              onClick: () => setSort("due")
            },
            {
              label: `${sort === "overdueTop" ? "* " : ""}${t("deadlines.sort.overdueTop")}`,
              onClick: () => setSort("overdueTop")
            },
            {
              label: t("deadlines.clearAllDone"),
              danger: true,
              onClick: () => done.forEach((d) => unDone(d.uid))
            }
          ]}
        />
      </div>

      <div className="row wrap tabs" style={{ marginBottom: 12 }}>
        <button className={`tab ${tab === "active" ? "is-active" : ""}`} onClick={() => setTab("active")}>
          {t("deadlines.active")} <span className="muted">({active.length})</span>
        </button>
        <button className={`tab ${tab === "done" ? "is-active" : ""}`} onClick={() => setTab("done")}>
          {t("deadlines.done")} <span className="muted">({done.length})</span>
        </button>
      </div>

      {list.length === 0 ? (
        <div className="empty">
          <div className="empty-title">{tab === "active" ? t("deadlines.empty.active") : t("deadlines.empty.done")}</div>
          <div className="muted small">{t("deadlines.empty.sub")}</div>
        </div>
      ) : (
        <div className="deadline-list">
          {list.map((d) => {
            const dueIso = d.dueAt;
            const overdue = isOverdue(dueIso, now);
            const countdown = formatCountdown(dueIso, now);
            const hasDesc = Boolean(d.description && d.description.trim());
            const isExpanded = hasDesc && expanded.has(d.uid);
            const desc = d.description || "";
            const urls = hasDesc ? extractUrls(desc) : [];
            const imageUrls = urls.filter(isImageUrl);
            const linkUrls = urls.filter((u) => !isImageUrl(u));
            const descText = hasDesc ? desc.replace(/https?:\/\/[^\s)>\]]+/gi, "").trim() : "";

            return (
              <div key={d.uid} className={`deadline-card ${overdue ? "is-overdue" : ""}`}>
                <div className="due">
                  <div className="time">{fmtWhen(dueIso)}</div>
                  {overdue ? <span className="badge">{t("deadlines.overdue")}</span> : null}
                  {countdown ? (
                    <div className={`countdown ${overdue ? "is-overdue" : ""}`}>{countdown}</div>
                  ) : null}
                  {d.room ? (
                    <button className="link" onClick={() => openMap(d.room)}>
                      {d.room}
                    </button>
                  ) : null}
                </div>

                <div className="content">
                  <div className="title-row">
                    <div className="title">{d.title}</div>

                    <div className="title-actions">
                      {d.moodleUrl ? (
                        <button className="btn ghost" onClick={() => openExternalUrl(d.moodleUrl!)}>
                          {t("common.openInMoodle")}
                        </button>
                      ) : null}

                      {hasDesc ? (
                        <button className="btn ghost" onClick={() => toggleDesc(d.uid)}>
                          {isExpanded ? t("common.hideDescription") : t("common.showDescription")}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="desc">
                      {descText ? <div className="desc-text">{descText}</div> : null}
                      {imageUrls.length ? (
                        <div className="desc-images">
                          {imageUrls.map((src) => (
                            <img key={src} src={src} alt="" loading="lazy" />
                          ))}
                        </div>
                      ) : null}
                      {linkUrls.length ? (
                        <div className="desc-links">
                          {linkUrls.map((href) => (
                            <button key={href} className="link" onClick={() => openExternalUrl(href)}>
                              {href}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="meta">
                    {d.courseName ? <span className="pill pill-off">{d.courseName}</span> : null}
                    {d.kind === "assignment" ? (
                      <span className="pill pill-on">{t("deadlines.kind.assignment")}</span>
                    ) : (
                      <span className="pill pill-on">{t("deadlines.kind.deadline")}</span>
                    )}
                  </div>

                  <div className="actions">
                    {tab === "active" ? (
                      <button className="btn primary done-btn" onClick={() => markDone(d.uid)}>
                        {t("deadlines.markDone")}
                      </button>
                    ) : (
                      <button className="btn ghost" onClick={() => unDone(d.uid)}>
                        {t("deadlines.undo")}
                      </button>
                    )}

                    {d.room ? (
                      <button className="btn ghost" onClick={() => openMap(d.room!)}>
                        {t("common.openMap")}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
